import { useState, useEffect } from 'react';

import Stack from '@mui/material/Stack';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';

import {
  getReportBranches,
  getReportCompanies,
  getReportEmployees,
  type BranchOption,
  type CompanyOption,
  type EmployeeOption,
} from 'src/api/reports.api';

import { useReportScope } from 'src/routes/context/ReportScopeContext';

type Props = {
  /** Çalışan seçicisini göster (tek-personel raporu için). Varsayılan: false. */
  showEmployee?: boolean;
  /** "Tümü" seçeneği yerine seçim zorunlu olsun mu (örn. aylık rapor şube ister). */
  requireBranch?: boolean;
};

/**
 * Kademeli rol-bilinçli rapor filtresi: Firma → Şube → Çalışan.
 * Seçenekler backend tarafından kullanıcının rolüne göre daraltılır; tek seçenek
 * dönerse otomatik seçilir ve kilitlenir (ADMIN/tek-şube MANAGER bu sayede sabitlenir).
 */
export function ReportScopeFilter({ showEmployee = false, requireBranch = false }: Props) {
  const { companyId, branchId, employeeId, setCompanyId, setBranchId, setEmployeeId } =
    useReportScope();

  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [branches, setBranches] = useState<BranchOption[]>([]);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);

  // Firmaları yükle (mount). Tek firma varsa otomatik seç + kilitle.
  useEffect(() => {
    let active = true;
    getReportCompanies()
      .then((list) => {
        if (!active) return;
        setCompanies(list);
        // Tek firma varsa otomatik seç — AMA yalnızca henüz seçim yoksa.
        // (Aksi halde sayfalar arası taşınan kapsamı sıfırlar; setCompanyId cascade reset yapar.)
        if (list.length === 1 && !companyId) setCompanyId(list[0].id);
      })
      .catch(() => active && setCompanies([]));
    return () => {
      active = false;
    };
    // setCompanyId context'ten kararlı; yalnızca mount'ta çalışsın
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Seçili firmaya göre şubeleri yükle. Tek şube varsa otomatik seç.
  useEffect(() => {
    let active = true;
    getReportBranches(companyId ?? undefined)
      .then((list) => {
        if (!active) return;
        setBranches(list);
        // Tek şube varsa otomatik seç — yalnızca henüz seçim yoksa (taşınan kapsamı koru).
        if (list.length === 1 && !branchId) setBranchId(list[0].id);
      })
      .catch(() => active && setBranches([]));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Seçili şubeye göre çalışanları yükle.
  useEffect(() => {
    if (!showEmployee || !branchId) {
      setEmployees([]);
      return undefined;
    }
    let active = true;
    getReportEmployees(branchId)
      .then((list) => active && setEmployees(list))
      .catch(() => active && setEmployees([]));
    return () => {
      active = false;
    };
  }, [branchId, showEmployee]);

  const lockCompany = companies.length <= 1;
  const lockBranch = branches.length <= 1 && branches.length > 0;

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
      <FormControl size="small" sx={{ minWidth: 220 }} disabled={lockCompany && companies.length === 1}>
        <InputLabel shrink>Firma</InputLabel>
        <Select
          value={companyId ?? ''}
          label="Firma"
          displayEmpty
          renderValue={(val) =>
            val ? companies.find((c) => c.id === val)?.name ?? '' : 'Tümü Firmalar'
          }
          onChange={(e) => setCompanyId(e.target.value ? String(e.target.value) : null)}
        >
          <MenuItem value="">
            <em>Tümü Firmalar</em>
          </MenuItem>
          {companies.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 220 }} disabled={lockBranch}>
        <InputLabel shrink>Şube</InputLabel>
        <Select
          value={branchId ?? ''}
          label="Şube"
          displayEmpty
          renderValue={(val) =>
            val
              ? branches.find((b) => b.id === val)?.name ?? ''
              : requireBranch
                ? 'Şube seçin'
                : 'Tümü Şubeler'
          }
          onChange={(e) => setBranchId(e.target.value ? String(e.target.value) : null)}
        >
          {!requireBranch && (
            <MenuItem value="">
              <em>Tümü Şubeler</em>
            </MenuItem>
          )}
          {branches.map((b) => (
            <MenuItem key={b.id} value={b.id}>
              {b.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {showEmployee && (
        <FormControl size="small" sx={{ minWidth: 220 }} disabled={!branchId}>
          <InputLabel shrink>Çalışan</InputLabel>
          <Select
            value={employeeId ?? ''}
            label="Çalışan"
            displayEmpty
            renderValue={(val) =>
              val ? employees.find((emp) => emp.id === val)?.fullName ?? '' : 'Tümü Çalışanlar'
            }
            onChange={(e) => setEmployeeId(e.target.value ? String(e.target.value) : null)}
          >
            <MenuItem value="">
              <em>Tümü Çalışanlar</em>
            </MenuItem>
            {employees.map((emp) => (
              <MenuItem key={emp.id} value={emp.id}>
                {emp.fullName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Stack>
  );
}
